/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { TGenericModelNode } from "@adapptio/lib-blueprint";
import { TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { isModelSchemaOf } from "../../common/ModelHelpers";
import { IFieldRuntimeError, IFieldValidationError } from "../components/Fields/FieldTypes";
import { ModelRuntimeContext } from "../contexts/ModelRuntimeContext";

export interface IResolvedModel<TModel extends TGenericModelNode> {
	model: TModel;
	isValid: boolean;
	validationErrors: IFieldValidationError[];
	runtimeErrors: IFieldRuntimeError[];
	updateRef?: number;
}

function resolveModel<TModel extends TGenericModelNode>(
	model: TModel,
	schemaName: string | string[],
	t: TTranslateHookFn
): IResolvedModel<TModel> {
	if (!model) {
		return {
			model: null,
			isValid: null,
			validationErrors: [],
			runtimeErrors: []
		};
	}

	if (schemaName && !isModelSchemaOf(model, schemaName)) {
		return {
			model: null,
			isValid: null,
			validationErrors: [],
			runtimeErrors: []
		};
	}

	return {
		model: model as unknown as TModel,
		isValid: model.isValid,
		validationErrors: Array.isArray(model.validationErrors)
			? model.validationErrors.map((error) => {
					return {
						message: error.metaData?.translationTerm
							? t("editor", error.metaData.translationTerm, error.metaData?.args)
							: error.message
					};
			  })
			: [],
		runtimeErrors: []
	};
}

/**
 * Hook to resolve and validate blueprint model. Also listens for model changes.
 * Return null if no model is provided or schema does not match.
 *
 * @param model Model node
 * @param schemaName Schema name
 */
export function useBlueprintModel<TModel extends TGenericModelNode>(
	model: TModel,
	schemaName?: string | string[]
): IResolvedModel<TModel> {
	const t = useTranslate();
	const modelRuntimeCtx = useContext(ModelRuntimeContext);

	const [ updateRef, setUpdateRef ] = useState(0);
	const [ runtimeErrors, setRuntimeErrors ] = useState<IFieldRuntimeError[]>([]);
	const hadRuntimeErrors = useRef(false);

	const modelState = useMemo(
		() => resolveModel<TModel>(model, schemaName, t),
		[ model, schemaName, updateRef ]
	);

	useEffect(() => {
		if (!modelState.model) {
			return;
		}

		const handleModelChange = () => {
			setUpdateRef((x) => x + 1);
		};

		onEvent(modelState.model.changeEvent, handleModelChange);

		return () => {
			offEvent(modelState.model.changeEvent, handleModelChange);
		};
	}, [ modelState.model ]);

	useEffect(() => {
		if (!modelRuntimeCtx || !model) {
			return;
		}

		const handleRender = () => {
			const _runtimeErrors = modelRuntimeCtx.modelRuntimeErrors.get(model.nodeId);

			if (Array.isArray(_runtimeErrors)) {
				setRuntimeErrors(
					_runtimeErrors.map((err) => {
						return {
							message: err.metaData?.translationTerm
								? t("editor", err.metaData.translationTerm, err.metaData?.args)
								: err.message,
							details: err.details,
							originalErrors: err
						};
					})
				);

				hadRuntimeErrors.current = true;
			} else if (hadRuntimeErrors.current) {
				setRuntimeErrors([]);
				hadRuntimeErrors.current = false;
			}
		};

		onEvent(modelRuntimeCtx.onRender, handleRender);
		handleRender();

		return () => {
			offEvent(modelRuntimeCtx.onRender, handleRender);
		};
	}, [ modelState.model, modelRuntimeCtx, hadRuntimeErrors ]);

	return {
		...modelState,
		updateRef: updateRef,
		runtimeErrors: runtimeErrors
	};
}

/**
 * Hook to resolve multiple blueprint models. Also listens for model changes.
 * Return array of model states. If no model is provided the state for given index is null.
 *
 * @param model Model node list
 */
export function useMultipleBlueprintModels<TModel extends TGenericModelNode>(
	models: TModel[]
): IResolvedModel<TModel>[] {
	const t = useTranslate();

	const [ , setUpdateRef ] = useState(0);

	// Models are dynamic-length array which cannot be passed directly to React.dependencyList
	// so we have to create a unique value
	const modelsDependencySignature = models.map((model) => (model ? model.nodeId : "n")).join(",");

	const modelState = useMemo(
		() => models.map((model) => resolveModel<TModel>(model, undefined, t)),
		[ modelsDependencySignature ]
	);

	useEffect(() => {
		const _models = modelState.slice();

		const _eventHandlers = _models.map((modelStateItem) => {
			if (!modelStateItem.model) {
				return null;
			}

			const handleModelChange = () => {
				setUpdateRef((x) => {
					modelStateItem.updateRef = x + 1;
					return x + 1;
				});
			};

			onEvent(modelStateItem.model.changeEvent, handleModelChange);

			return handleModelChange;
		});

		return () => {
			_eventHandlers.forEach((handler, index) => {
				if (handler !== null) {
					offEvent(modelState[index].model.changeEvent, handler);
				}
			});
		};
	}, [ modelState ]);

	return modelState;
}
